import { Injectable, signal, computed, inject } from '@angular/core';
import { FileStorageService } from './file-storage.service';
import { firstValueFrom } from 'rxjs';

export type DocumentType = 'pdf' | 'image';
export type DocumentSource = 'load' | 'stop';

export interface ViewableDocument {
  id: string;
  url: string;
  type: DocumentType;
  title: string;
  source: DocumentSource;
  sourceId?: string; // stopNumber for stop documents
  documentCategory?: string; // ADD: For better file naming (rateConfirmation, dispatchSheet, etc.)
}

@Injectable({ providedIn: 'root' })
export class DocumentViewerService {
  private readonly fileStorageService = inject(FileStorageService);

  private _activeDocument = signal<ViewableDocument | null>(null);
  private _isFullscreen = signal(false);
  private _isDownloading = signal(false); // ADD: Download state

  // Public read-only signals
  activeDocument = this._activeDocument.asReadonly();
  isFullscreen = this._isFullscreen.asReadonly();
  isDownloading = this._isDownloading.asReadonly(); // ADD: Expose download state

  // Computed signals for convenience
  hasActiveDocument = computed(() => !!this._activeDocument());
  activeDocumentType = computed(() => this._activeDocument()?.type);

  // ADD: Computed for download filename
  downloadFilename = computed(() => {
    const doc = this._activeDocument();
    if (!doc) return 'document';

    return this.generateFileName(doc);
  });

  setActiveDocument(document: ViewableDocument | null) {
    this._activeDocument.set(document);
  }

  updateActiveDocumentIfMatches(
    loadId: string,
    documentType: string,
    newUrl: string
  ) {
    const currentDoc = this._activeDocument();
    if (
      currentDoc &&
      currentDoc.id.includes(loadId) &&
      currentDoc.id.includes(documentType)
    ) {
      this.setActiveDocument({
        ...currentDoc,
        url: newUrl,
      });
    }
  }

  toggleFullscreen() {
    this._isFullscreen.update((current) => !current);
  }

  clearActiveDocument() {
    this._activeDocument.set(null);
    this._isFullscreen.set(false);
  }

  // ADD: Download functionality
  async downloadActiveDocument(): Promise<void> {
    const doc = this._activeDocument();
    if (!doc) return;

    this._isDownloading.set(true);

    try {
      // Get the file blob
      const blob = await firstValueFrom(
        this.fileStorageService.getFileAsBlob(doc.url)
      );
      if (!blob) {
        throw new Error('Failed to fetch document blob');
      }

      // Generate filename
      const filename = this.generateFileName(doc);

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } finally {
      this._isDownloading.set(false);
    }
  }

  // ADD: Generate appropriate filename based on document type
  private generateFileName(doc: ViewableDocument): string {
    // Extract load ID from document ID or title
    const loadIdMatch = doc.id.match(/load-([^-]+)/);
    const loadId = loadIdMatch ? loadIdMatch[1] : 'unknown';

    // Get file extension
    const extension = doc.type === 'pdf' ? 'pdf' : 'jpg';

    // Generate filename based on document category
    if (
      doc.id.includes('rateConfirmation') ||
      doc.title.toLowerCase().includes('rate confirmation')
    ) {
      return `RC-${loadId}.${extension}`;
    } else if (
      doc.id.includes('dispatchSheet') ||
      doc.id.includes('latestDispatchSheet') ||
      doc.title.toLowerCase().includes('dispatch sheet')
    ) {
      return `DispatchSheet-${loadId}.${extension}`;
    } else if (
      doc.id.includes('invoice') ||
      doc.title.toLowerCase().includes('invoice')
    ) {
      return `Invoice-${loadId}.${extension}`;
    } else if (doc.source === 'stop') {
      const stopNumber = doc.sourceId || 'unknown';
      return `LoadImage-${loadId}-Stop${stopNumber}.${extension}`;
    } else {
      // Fallback for unknown document types
      return `Document-${loadId}.${extension}`;
    }
  }
}
